import type { HTMLAttributeAnchorTarget } from 'react'

import { AnyObject } from '../utils'
import type { FilterFormType } from './filter'
import type {
    OpenWechatCustomerServiceConfig,
    SourceRecordConfig,
    StripePayConfig,
    SubProcessArg,
    WechatNavigateToMiniProgramConfig,
    WechatOpenChannelsActivityConfig,
    WechatPayConfig,
    WechatSubscriptionMsgNotificationConfig,
    WechatTemplateMsgConfig
} from './flow'
import type { PageStackDisplayType } from './pageStack'
import type { NodeFieldContent, RichTextContentProtocol } from './richtext'
import type { VariableADTvalue } from './variable'

export enum ActionControlType {
    REFRESH = 'refresh',
    PAGINATE_TO = 'paginateTo',
    HORIZONTAL_SCROLL_TO = 'horizontalScrollTo',
    SCROLL_TO_RECORD = 'scrollToRecord',
    LOAD_MORE = 'loadMore',
    SWITCH_PANEL = 'switchPanel',
    OPEN = 'open',
    CLOSE = 'close',
    CLEAR = 'clear',
    FOCUS = 'focus'
}

export enum ActionHandleEventType {
    ACTION = 'flow',
    AUTOMATION = 'automation',
    OPEN_PAGE = 'openPage',
    OPEN_LINK = 'openLink',
    SCROLL_TO = 'scrollTo',
    CONTROL = 'control',
    GO_BACK = 'goBack',
    NOTIFICATION = 'notification',
    COPY = 'copy',
    CREATE_RECORD = 'createRecord',
    UPDATE_RECORD = 'updateRecord',
    DELETE_RECORD = 'deleteRecord',
    DOWNLOAD_FILE = 'downloadFile',
    ALIYUN_MESSAGE = 'aliyunMessage',
    CALL = 'call',
    WECHAT_PAY = 'wechatPay',
    WECHAT_TEMPLATE_MSG = 'wechatTemplateMsg',
    STRIPE = 'stripe',
    UPDATE_PAGE_STATE = 'updatePageState',
    REFRESH_PAGE = 'refreshPage',
    CLOSE_FLOAT = 'closeFloat',
    SCREEN_SHOT = 'screenShot',
    WECHAT_SUBSCRIPTION_MSG = 'wechatSubscriptionMsg',
    WECHAT_SUBSCRIPTION_MSG_NOTIFICATION = 'wechatSubscriptionMsgNotification',
    OPEN_WECHAT_CUSTOMER_SERVICE = 'openWechatCustomerService',
    WECHAT_NAVIGATE_TO_MINI_PROGRAM = 'wechatNavigateToMiniProgram',
    UPDATE_QUERY_PARAMETER = 'updateQueryParameter',
    WECHAT_OPEN_CHANNELS_ACTIVITY = 'wechatOpenChannelsActivity',
    UPDATE_PAGE_SHARE = 'updatePageShare'
}

export type ActionTriggerTypeProtocols = {
    id: string
}

export type ActionExecuteFilter = {
    executeFilter: FilterFormType
}

export type ActionRunConfig = {
    config?: ActionExecuteFilter
}

export type InputFlowArg = {
    flowId: string
    args: SubProcessArg[]
}

export type OpenPageActionConfig = {
    openPageId: string
    pageOpenType: PageStackDisplayType
    hash?: string
    pathParameters?: { id: string; value?: RichTextContentProtocol }[]
    queryParameters?: { id: string; value?: RichTextContentProtocol }[]
}

export type ViewControlLoadMoreAction = {
    type: ActionControlType.LOAD_MORE
}

export type FlowAction = ActionRunConfig & {
    type: ActionHandleEventType.ACTION
    data: {
        flow: InputFlowArg
    }
}

export type AutomationAction = ActionRunConfig & {
    type: ActionHandleEventType.AUTOMATION
    data: {
        automation: InputFlowArg
    }
}

// 打开页面
export type OpenPageAction = ActionRunConfig & {
    type: ActionHandleEventType.OPEN_PAGE
    data: {
        openPage: OpenPageActionConfig
    }
}

// 打开链接
export type OpenLinkAction = ActionRunConfig & {
    type: ActionHandleEventType.OPEN_LINK
    data: {
        openLink: {
            link: AnyObject
            target?: HTMLAttributeAnchorTarget
        }
    }
}

// 滚动到
export type ScrollToAction = ActionRunConfig & {
    type: ActionHandleEventType.SCROLL_TO
    data: {
        scrollTo: {
            scrollToElementId: string
        }
    }
}

// 截图
export type ScreenShotAction = ActionRunConfig & {
    type: ActionHandleEventType.SCREEN_SHOT
    data: {
        screenShot: {
            screenShotElementId: string
            scale: string
        }
    }
}

/**
 * 视图控制动作
 */
export type ViewControlRefreshAction = ActionRunConfig & {
    type: ActionControlType.REFRESH
}
export type ViewControlPaginateToAction = ActionRunConfig & {
    type: ActionControlType.PAGINATE_TO
    params: {
        paginateToType: 'next' | 'prev'
    }
}

export type ViewControlHorizontalScrollToAction = {
    type: ActionControlType.HORIZONTAL_SCROLL_TO
    params: {
        horizontalScrollDirection: 'left' | 'right'
        horizontalScrollTo: number | 'end'
    }
}
export type ViewControlScrollToRecordAction<TRichText = RichTextContentProtocol> = {
    type: ActionControlType.SCROLL_TO_RECORD
    params: {
        scrollToRecord: TRichText
    }
}
export type ViewControlAction<TRichText = RichTextContentProtocol> =
    | ViewControlRefreshAction
    | ViewControlPaginateToAction
    | ViewControlHorizontalScrollToAction
    | ViewControlScrollToRecordAction<TRichText>
    | ViewControlLoadMoreAction

/**
 * 容器控制动作
 * @template TNthPanel - 存储时为变量 ADT；运行时（监听器）在解析后为 finite number
 */
export type ContainerControlSwitchPanelAction<TNthPanel = VariableADTvalue> = {
    type: ActionControlType.SWITCH_PANEL
    params: {
        switchPanelId: string
        autoForwardLoop?: boolean
        loopInterval?: number
        nthPanel?: TNthPanel
    }
}
export type ContainerControlOpenAction = {
    type: ActionControlType.OPEN
}
export type ContainerControlCloseAction = {
    type: ActionControlType.CLOSE
}
export type ContainerControlAction<TNthPanel = VariableADTvalue> =
    | ContainerControlSwitchPanelAction<TNthPanel>
    | ContainerControlOpenAction
    | ContainerControlCloseAction

/** 运行时 blockListener 收到的 switchPanel params（nthPanel 已由动作引擎解析） */
export type ResolvedContainerControlSwitchPanelParams = ContainerControlSwitchPanelAction<number>['params']

/**
 * 字段控制动作
 */
export type FieldControlClearAction = {
    type: ActionControlType.CLEAR | ActionControlType.FOCUS
}

export type FieldControlAction = FieldControlClearAction

/**
 * 控制动作参数类型
 * @template TRichText - RichText 的类型，默认为 RichTextContentProtocol（存储格式）
 *                       运行时可传入 string（解析后的格式）
 * @template TNthPanel - 容器「第几个面板」序号：配置为 VariableADTvalue，监听器侧为 number
 */
export type ControlActionParams<TRichText = RichTextContentProtocol, TNthPanel = VariableADTvalue> =
    | ViewControlAction<TRichText>
    | ContainerControlAction<TNthPanel>
    | FieldControlAction

/**
 * 运行时控制动作参数（RichText 已解析为 string；switchPanel 的 nthPanel 已解析为 number）
 * 用于 blockListenerSystem 的监听器类型推导
 */
export type ResolvedControlActionParams = ControlActionParams<string, number>

export type ContainerControlActionWithPayload<TNthPanel = VariableADTvalue> = {
    blockType: 'container'
    payload: ContainerControlAction<TNthPanel>
}

export type ViewControlActionWithPayload<TRichText = RichTextContentProtocol> = {
    blockType: 'view'
    payload: ViewControlAction<TRichText>
}

export type FieldControlActionWithPayload = {
    blockType: 'field'
    payload: FieldControlAction
}

export type ControlActionWithPayload<TRichText = RichTextContentProtocol, TNthPanel = VariableADTvalue> =
    | ContainerControlActionWithPayload<TNthPanel>
    | ViewControlActionWithPayload<TRichText>
    | FieldControlActionWithPayload

/**
 * 运行时的 ControlActionWithPayload（RichText 已解析为 string；nthPanel 已解析为 number）
 * 用于 blockListenerSystem 的监听器类型推导
 */
export type ResolvedControlActionWithPayload = ControlActionWithPayload<string, number>

export type ControlledActionBlockType = ControlActionWithPayload['blockType']

export type ControlAction = ActionRunConfig & {
    type: ActionHandleEventType.CONTROL
    data: {
        control: {
            controlElementId: string
            controlElementType: 'view' | 'container' | 'field'
            controlAction?: ControlActionParams
        }
    }
}

// 返回上一步
export type GoBackAction = ActionRunConfig & {
    type: ActionHandleEventType.GO_BACK
    data: {
        goBack: {}
    }
}

// 通知
export type NotificationAction = ActionRunConfig & {
    type: ActionHandleEventType.NOTIFICATION
    data: {
        notification: {
            notificationContent: AnyObject // TiptapEditorJSONContent
            notificationStatus: 'SUCCESS' | 'ERROR' | 'INFO'
        }
    }
}

// 拷贝
export type CopyAction = ActionRunConfig & {
    type: ActionHandleEventType.COPY
    data: {
        copy: {
            copyText: AnyObject
        }
    }
}

// 拨打电话
export type CallAction = ActionRunConfig & {
    type: ActionHandleEventType.CALL
    data: {
        call: {
            phone: VariableADTvalue // TiptapEditorJSONContent
        }
    }
}

// 创建行
export type CreateRecordAction = ActionRunConfig & {
    type: ActionHandleEventType.CREATE_RECORD
    data: {
        createRecord: {
            dataSourceId: string
            fields: []
        }
    }
}

export type UpdateRecordActionConfig = SourceRecordConfig & {
    fields: NodeFieldContent[]
}
// 更新行
export type UpdateRecordAction = ActionRunConfig & {
    type: ActionHandleEventType.UPDATE_RECORD
    data: {
        updateRecord: UpdateRecordActionConfig
    }
}

export type DeleteRecordActionConfig = SourceRecordConfig & {
    confirm?: {
        enable?: boolean
        title?: RichTextContentProtocol
        description?: RichTextContentProtocol
        confirmText?: RichTextContentProtocol
        cancelText?: RichTextContentProtocol
    }
}
// 删除行
export type DeleteRecordAction = ActionRunConfig & {
    type: ActionHandleEventType.DELETE_RECORD
    data: {
        deleteRecord: DeleteRecordActionConfig
    }
}

export type DownloadFileAction = ActionRunConfig & {
    type: ActionHandleEventType.DOWNLOAD_FILE
    data: {
        downloadFile: {
            fileUrl: VariableADTvalue
        }
    }
}

export type AliyunMessageAction = ActionRunConfig & {
    type: ActionHandleEventType.ALIYUN_MESSAGE
    data: {
        aliyunMessage: {
            id: string
            mobileList: VariableADTvalue
            signatureId: string
            templateId: string
            values: { variableName: string; value: RichTextContentProtocol }[]
        }
    }
}

export type WeChatPayAction = ActionRunConfig & {
    type: ActionHandleEventType.WECHAT_PAY
    data: {
        wechatPay: WechatPayConfig
    }
}

export type WechatTemplateMsgAction = ActionRunConfig & {
    type: ActionHandleEventType.WECHAT_TEMPLATE_MSG
    data: {
        wechatTemplateMsg: WechatTemplateMsgConfig
    }
}

export type RefreshPageAction = ActionRunConfig & {
    type: ActionHandleEventType.REFRESH_PAGE
    data: {
        refreshPage: {}
    }
}

export type StripeAction = ActionRunConfig & {
    type: ActionHandleEventType.STRIPE
    data: {
        stripe: StripePayConfig
    }
}

// 返回上一步
export type CloseFloatAction = ActionRunConfig & {
    type: ActionHandleEventType.CLOSE_FLOAT
    data: {
        closeFloat: {}
    }
}

// 微信小程序订阅消息
export type WechatSubscriptionMsgAction = ActionRunConfig & {
    type: ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG
    data: {
        wechatSubscriptionMsg: {
            templateIds: string[]
        }
    }
}

// 微信小程序发送订阅消息
export type WechatSubscriptionMsgNotificationAction = ActionRunConfig & {
    type: ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION
    data: {
        wechatSubscriptionMsgNotification: WechatSubscriptionMsgNotificationConfig
    }
}

export type OpenWechatCustomerServiceAction = ActionRunConfig & {
    type: ActionHandleEventType.OPEN_WECHAT_CUSTOMER_SERVICE
    data: {
        openWechatCustomerService: OpenWechatCustomerServiceConfig
    }
}

// 跳转到微信小程序
export type WechatNavigateToMiniProgramAction = ActionRunConfig & {
    type: ActionHandleEventType.WECHAT_NAVIGATE_TO_MINI_PROGRAM
    data: {
        wechatNavigateToMiniProgram: WechatNavigateToMiniProgramConfig
    }
}

// 跳转微信视频
export type WechatOpenChannelsActivityAction = ActionRunConfig & {
    type: ActionHandleEventType.WECHAT_OPEN_CHANNELS_ACTIVITY
    data: {
        wechatOpenChannelsActivity: WechatOpenChannelsActivityConfig
    }
}

export type UpdatePageStateAction = ActionRunConfig & {
    type: ActionHandleEventType.UPDATE_PAGE_STATE
    data: {
        updatePageState: {
            pageStateId?: string
            value?: RichTextContentProtocol
        }
    }
}

export type UpdateQueryParameterAction = ActionRunConfig & {
    type: ActionHandleEventType.UPDATE_QUERY_PARAMETER
    data: {
        updateQueryParameter: {
            queryParameterId?: string
            value?: RichTextContentProtocol
        }
    }
}

export type UpdatePageShareAction = ActionRunConfig & {
    type: ActionHandleEventType.UPDATE_PAGE_SHARE
    data: {
        updatePageShare: {
            image?: VariableADTvalue
            title?: RichTextContentProtocol
            description?: RichTextContentProtocol
            link?: RichTextContentProtocol
        }
    }
}

export type ActionParams =
    | OpenPageAction
    | OpenLinkAction
    | ScrollToAction
    | ControlAction
    | NotificationAction
    | CopyAction
    | GoBackAction
    | CreateRecordAction
    | UpdateRecordAction
    | DeleteRecordAction
    | FlowAction
    | AutomationAction
    | DownloadFileAction
    | AliyunMessageAction
    | CallAction
    | WeChatPayAction
    | WechatTemplateMsgAction
    | StripeAction
    | RefreshPageAction
    | CloseFloatAction
    | UpdatePageStateAction
    | ScreenShotAction
    | WechatSubscriptionMsgAction
    | WechatSubscriptionMsgNotificationAction
    | OpenWechatCustomerServiceAction
    | WechatNavigateToMiniProgramAction
    | WechatOpenChannelsActivityAction
    | UpdateQueryParameterAction
    | UpdatePageShareAction

export type IndependentAction = ActionTriggerTypeProtocols & ActionParams

export type IndependentActionType = IndependentAction['type']
